<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.WA.2 — "Add WhatsApp" (canvas: Whatsapp). Uses the
// existing Kaption2FA enrollment endpoints; the server enforces the cooldown.
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KaptionWhatsapp2faAPI from 'dashboard/api/kaptionWhatsapp2fa';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import KaptionTwoStepCodeInput from './KaptionTwoStepCodeInput.vue';

const emit = defineEmits(['added']);
const { t } = useI18n();

const COOLDOWN = 60;
const dialogRef = ref(null);
const codeInputRef = ref(null);
const phone = ref('');
const code = ref('');
const sent = ref(false);
const cooldownLeft = ref(0);
const busy = ref(false);
const error = ref('');
let timer = null;

const startCooldown = (seconds = COOLDOWN) => {
  cooldownLeft.value = seconds;
  clearInterval(timer);
  timer = setInterval(() => {
    cooldownLeft.value -= 1;
    if (cooldownLeft.value <= 0) clearInterval(timer);
  }, 1000);
};
onBeforeUnmount(() => clearInterval(timer));

const open = async () => {
  code.value = '';
  sent.value = false;
  error.value = '';
  busy.value = false;
  dialogRef.value?.open();
  try {
    const { data } = await KaptionWhatsapp2faAPI.getStatus();
    phone.value = data.prefill_phone || '';
  } catch (e) {
    // the person types their number
  }
};

const close = () => dialogRef.value?.close();

const sendCode = async () => {
  if (busy.value || !phone.value.trim()) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await KaptionWhatsapp2faAPI.sendCode(phone.value);
    if (data?.phone) phone.value = data.phone;
    sent.value = true;
    startCooldown();
    codeInputRef.value?.focus();
  } catch (e) {
    const retry = e?.response?.data?.retry_after;
    if (retry) startCooldown(Number(retry));
    error.value =
      e?.response?.data?.error || t('KAPTION_TWO_STEP.ERRORS.GENERIC');
  } finally {
    busy.value = false;
  }
};

const verify = async () => {
  if (busy.value || code.value.length !== 6) return;
  busy.value = true;
  error.value = '';
  try {
    await KaptionWhatsapp2faAPI.verify(code.value);
    const { data } = await KaptionTwoStepAPI.getStatus();
    close();
    emit('added', { method: 'whatsapp', status: data });
  } catch (e) {
    error.value =
      e?.response?.data?.error ||
      t('KAPTION_TWO_STEP.LOGIN.ERRORS.INVALID_CODE');
    codeInputRef.value?.clear();
  } finally {
    busy.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="sent ? verify() : sendCode()"
  >
    <div class="flex flex-col gap-5" data-testid="kpt-two-step-whatsapp-dialog">
      <div class="flex flex-col gap-2">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.WA_DIALOG.TITLE') }}
        </h3>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('KAPTION_TWO_STEP.WA_DIALOG.DESC') }}
        </p>
      </div>
      <div class="flex flex-col gap-1.5">
        <label for="kpt-wa-phone" class="text-sm font-medium text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.WA_DIALOG.PHONE_LABEL') }}
        </label>
        <div class="flex gap-2">
          <Input
            id="kpt-wa-phone"
            v-model="phone"
            type="tel"
            class="flex-1"
            :placeholder="$t('KAPTION_TWO_STEP.WA_DIALOG.PHONE_PLACEHOLDER')"
            data-testid="kpt-two-step-wa-phone"
          />
          <Button
            type="button"
            :faded="sent"
            :label="
              cooldownLeft > 0
                ? $t('KAPTION_TWO_STEP.WA_DIALOG.RESEND_IN', {
                    seconds: cooldownLeft,
                  })
                : sent
                  ? $t('KAPTION_TWO_STEP.WA_DIALOG.RESEND')
                  : $t('KAPTION_TWO_STEP.WA_DIALOG.SEND')
            "
            :disabled="busy || cooldownLeft > 0 || !phone.trim()"
            data-testid="kpt-two-step-wa-send"
            @click="sendCode"
          />
        </div>
        <span v-if="sent" class="text-[13px] text-n-teal-11">
          {{ $t('KAPTION_TWO_STEP.WA_DIALOG.SENT') }}
        </span>
      </div>
      <KaptionTwoStepCodeInput
        ref="codeInputRef"
        v-model="code"
        :label="$t('KAPTION_TWO_STEP.WA_DIALOG.CODE_LABEL')"
        :disabled="!sent || busy"
        @complete="verify"
      />
      <p v-if="error" class="text-sm text-n-ruby-11" role="alert">
        {{ error }}
      </p>
    </div>
    <template #footer>
      <div class="flex gap-3">
        <Button
          type="button"
          slate
          faded
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          type="button"
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.ACTIONS.VERIFY')"
          :is-loading="busy && sent"
          :disabled="busy || !sent || code.length !== 6"
          data-testid="kpt-two-step-wa-verify"
          @click="verify"
        />
      </div>
    </template>
  </Dialog>
</template>
